import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, ClipboardList, ArrowRight, Search, ChevronLeft, ChevronRight, MoreHorizontal, BarChart } from 'lucide-react';
import {
    submitMonthlyCollection,
    getMyMonthlyCollections,
    getMyMonthlyStatistics,
    updateMonthlyCollection,
    deleteMonthlyCollection
} from '../actions/monthlyCollectionActions';
import { getMyProcesses, getProcessDefaults } from '../actions/dailyCollectionActions';
import {
    SUBMIT_MONTHLY_COLLECTION_RESET,
    GET_MY_MONTHLY_COLLECTIONS_RESET,
    GET_MY_MONTHLY_STATISTICS_RESET,
    UPDATE_MONTHLY_COLLECTION_RESET,
    DELETE_MONTHLY_COLLECTION_RESET,
    GET_PROCESS_DEFAULTS_RESET
} from '../constants/monthlyCollectionConstants';

// Enhanced Pagination Component
const EnhancedPagination = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    className = "",
    showItemsPerPage = true,
    showInfo = true,
    maxVisiblePages = 5
}) => {
    const [itemsPerPageOptions] = useState([5, 10, 15, 20, 25, 50]);

    const getVisiblePages = () => {
        const pages = [];
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();
    const showStartEllipsis = visiblePages[0] > 2;
    const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages - 1;

    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        onItemsPerPageChange(newItemsPerPage);
        const newTotalPages = Math.ceil(totalItems / newItemsPerPage);
        if (currentPage > newTotalPages) {
            onPageChange(newTotalPages);
        }
    };

    if (totalPages <= 1 && !showInfo) return null;

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-6 bg-white border-t border-gray-200 ${className}`}>
            {showItemsPerPage && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <span>entries</span>
                </div>
            )}

            {showInfo && (
                <div className="text-sm text-gray-600 order-first sm:order-none">
                    {totalItems > 0 ? (
                        <>
                            Showing <span className="font-medium">{startItem}</span> to{' '}
                            <span className="font-medium">{endItem}</span> of{' '}
                            <span className="font-medium">{totalItems}</span> results
                        </>
                    ) : (
                        'No results found'
                    )}
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                    >
                        <ChevronLeft size={16} />
                        <span className="hidden sm:inline">Previous</span>
                    </button>

                    {visiblePages[0] > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange(1)}
                                className="w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md hover:bg-gray-50 hover:text-blue-600"
                            >
                                1
                            </button>
                            {showStartEllipsis && (
                                <div className="flex items-center justify-center w-10 h-10">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                        </>
                    )}

                    {visiblePages.map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md transition-colors ${page === currentPage
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {visiblePages[visiblePages.length - 1] < totalPages && (
                        <>
                            {showEndEllipsis && (
                                <div className="flex items-center justify-center w-10 h-10">
                                    <MoreHorizontal size={16} className="text-gray-400" />
                                </div>
                            )}
                            <button
                                onClick={() => onPageChange(totalPages)}
                                className="w-10 h-10 flex items-center justify-center text-sm font-medium rounded-md hover:bg-gray-50 hover:text-blue-600"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                            }`}
                    >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

const MonthlyCollection = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const submitMonthlyCollectionInfo = useSelector((state) => state.submitMonthlyCollectionInfo);
    const { loadingSubmitMonthlyCollection, errorSubmitMonthlyCollection, submitMonthlyCollectionData } = submitMonthlyCollectionInfo;

    const myMonthlyCollectionsInfo = useSelector((state) => state.myMonthlyCollectionsInfo);
    const { loadingMyMonthlyCollections, errorMyMonthlyCollections, myMonthlyCollectionsData } = myMonthlyCollectionsInfo;

    const myProcessesInfo = useSelector((state) => state.myProcessesInfo);
    const { myProcessesData } = myProcessesInfo;

    const myMonthlyStatisticsInfo = useSelector((state) => state.myMonthlyStatisticsInfo);
    const { loadingMyMonthlyStatistics, myMonthlyStatisticsData } = myMonthlyStatisticsInfo;

    const updateMonthlyCollectionInfo = useSelector((state) => state.updateMonthlyCollectionInfo);
    const { loadingUpdateMonthlyCollection, errorUpdateMonthlyCollection, updateMonthlyCollectionData } = updateMonthlyCollectionInfo;

    const deleteMonthlyCollectionInfo = useSelector((state) => state.deleteMonthlyCollectionInfo);
    const { loadingDeleteMonthlyCollection, errorDeleteMonthlyCollection, deleteMonthlyCollectionData } = deleteMonthlyCollectionInfo;

    const processDefaultsInfo = useSelector((state) => state.processDefaultsInfo);
    const { loadingProcessDefaults, errorProcessDefaults, processDefaultsData } = processDefaultsInfo;

    // Local state
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({
        process: '',
        case: '',
        pos: '',
        res: '',
        rescase: '',
        norm: '',
        rbPercentage: '',
        totalCollection: '',
        currentProvisionalBilling: '',
        finalProvisionalBilling: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        month: '',
        year: ''
    });

    // Process change handler
    const handleProcessChange = (e) => {
        const { name, value } = e.target;

        setFormData({ ...formData, [name]: value });

        if (name === 'process' && value && !isEditing) {
            dispatch(getProcessDefaults(value));
        }
    };

    // Effect for setting defaults when process defaults are loaded
    useEffect(() => {
        if (processDefaultsData && !isEditing && isOpen) {
            setFormData(prev => ({
                ...prev,
                case: processDefaultsData.case || prev.case,
                pos: processDefaultsData.pos || prev.pos
            }));
        }
    }, [processDefaultsData, isEditing, isOpen]);

    // Effect for when modal opens and process is selected
    useEffect(() => {
        if (isOpen && !isEditing && formData.process) {
            dispatch(getProcessDefaults(formData.process));
        }
    }, [isOpen, isEditing, formData.process, dispatch]);

    // Get processes on component mount
    useEffect(() => {
        if (userInfo) {
            dispatch(getMyProcesses());
            dispatch(getMyMonthlyStatistics());
        }
    }, [dispatch, userInfo]);

    // Set initial process when processes are loaded
    useEffect(() => {
        if (myProcessesData && myProcessesData.processes && myProcessesData.processes.length > 0) {
            const firstProcess = myProcessesData.processes[0]._id;
            setFormData(prev => ({
                ...prev,
                process: firstProcess
            }));

            if (isOpen && !isEditing) {
                dispatch(getProcessDefaults(firstProcess));
            }
        }
    }, [myProcessesData, isOpen, isEditing, dispatch]);

    // Fetch collections when filters change
    useEffect(() => {
        if (userInfo) {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (searchTerm) params.append('search', searchTerm);
            if (filters.month) params.append('month', filters.month);
            if (filters.year) params.append('year', filters.year);

            dispatch(getMyMonthlyCollections(params.toString()));
        }
    }, [dispatch, userInfo, page, limit, searchTerm, filters.month, filters.year]);

    // Handle submit collection success
    useEffect(() => {
        if (submitMonthlyCollectionData) {
            const selectedProcess = formData.process;
            setFormData({
                process: selectedProcess,
                case: '',
                pos: '',
                res: '',
                rescase: '',
                norm: '',
                rbPercentage: '',
                totalCollection: '',
                currentProvisionalBilling: '',
                finalProvisionalBilling: ''
            });

            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            dispatch(getMyMonthlyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyMonthlyCollections(params.toString()));

            setTimeout(() => {
                dispatch({ type: SUBMIT_MONTHLY_COLLECTION_RESET });
            }, 3000);
        }
    }, [submitMonthlyCollectionData, dispatch, formData.process, page, limit]);

    // Handle update collection success
    useEffect(() => {
        if (updateMonthlyCollectionData) {
            setIsEditing(false);
            setEditId(null);
            setIsOpen(false);

            dispatch(getMyMonthlyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyMonthlyCollections(params.toString()));

            setTimeout(() => {
                dispatch({ type: UPDATE_MONTHLY_COLLECTION_RESET });
            }, 3000);
        }
    }, [updateMonthlyCollectionData, dispatch, page, limit]);

    // Handle delete collection success
    useEffect(() => {
        if (deleteMonthlyCollectionData) {
            dispatch(getMyMonthlyStatistics());
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });
            dispatch(getMyMonthlyCollections(params.toString()));

            setTimeout(() => {
                dispatch({ type: DELETE_MONTHLY_COLLECTION_RESET });
            }, 3000);
        }
    }, [deleteMonthlyCollectionData, dispatch, page, limit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['case', 'pos', 'res', 'rescase', 'norm', 'rbPercentage', 'totalCollection', 'currentProvisionalBilling', 'finalProvisionalBilling'];

        if (name === 'process') {
            handleProcessChange(e);
        } else {
            const processedValue = numericFields.includes(name) ?
                (value === '' ? '' : Number(value)) : value;
            setFormData({ ...formData, [name]: processedValue });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isEditing) {
            dispatch(updateMonthlyCollection(editId, formData));
        } else {
            dispatch(submitMonthlyCollection(formData));
        }
    };

    const handleCloseModal = () => {
        setIsEditing(false);
        setEditId(null);

        const selectedProcess = myProcessesData?.processes?.length > 0 ? myProcessesData.processes[0]._id : '';
        setFormData({
            process: selectedProcess,
            case: '',
            pos: '',
            res: '',
            rescase: '',
            norm: '',
            rbPercentage: '',
            totalCollection: '',
            currentProvisionalBilling: '',
            finalProvisionalBilling: ''
        });

        setIsOpen(false);

        dispatch({ type: SUBMIT_MONTHLY_COLLECTION_RESET });
        dispatch({ type: UPDATE_MONTHLY_COLLECTION_RESET });
        dispatch({ type: GET_PROCESS_DEFAULTS_RESET });
    };

    const handleUpdate = (collection) => {
        setFormData({
            process: collection.process._id,
            case: collection.case,
            pos: collection.pos,
            res: collection.res,
            rescase: collection.rescase,
            norm: collection.norm || '',
            rbPercentage: collection.rbPercentage || '',
            totalCollection: collection.totalCollection,
            currentProvisionalBilling: collection.currentProvisionalBilling || '',
            finalProvisionalBilling: collection.finalProvisionalBilling || ''
        });

        setIsEditing(true);
        setEditId(collection._id);
        setIsOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this monthly collection?')) {
            dispatch(deleteMonthlyCollection(id));
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleItemsPerPageChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
        setPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const formatDate = (month, year) => {
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return `${monthNames[month - 1]} ${year}`;
    };

    const stats = myMonthlyStatisticsData?.stats || {
        totalCases: 0,
        totalPos: 0,
        totalRes: 0,
        totalResCase: 0,
        totalNorm: 0,
        totalRbPercentage: 0,
        totalCollection: 0,
        totalCurrentProvisionalBilling: 0,
        totalFinalProvisionalBilling: 0
    };

    const collections = myMonthlyCollectionsData?.data || [];
    const totalPages = myMonthlyCollectionsData?.totalPages || 1;
    const totalItems = myMonthlyCollectionsData?.total || 0;
    const assignedProcesses = myProcessesData?.processes || [];

    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Welcome Card */}
                {userInfo && (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                        <div className="p-6 bg-green-500 text-white">
                            <h1 className="text-2xl font-bold">Monthly Collection Dashboard</h1>
                            <p className="text-green-100">Submit your monthly collection data</p>
                        </div>
                    </div>
                )}

                {/* Statistics Card */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center">
                            <BarChart className="mr-2" size={20} />
                            Monthly Statistics
                        </h2>
                    </div>

                    <div className="p-6">
                        {loadingMyMonthlyStatistics ? (
                            <div className="text-center text-gray-500">Loading statistics...</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {stats && stats.totalCases > 0 && (
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <p className="text-sm text-blue-500">Total Cases</p>
                                        <p className="text-2xl font-bold text-blue-700">{stats.totalCases.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalPos > 0 && (
                                    <div className="bg-green-50 p-4 rounded-lg">
                                        <p className="text-sm text-green-500">Total POS</p>
                                        <p className="text-2xl font-bold text-green-700">{stats.totalPos.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalRes > 0 && (
                                    <div className="bg-purple-50 p-4 rounded-lg">
                                        <p className="text-sm text-purple-500">Total RES%</p>
                                        <p className="text-2xl font-bold text-purple-700">{stats.totalRes.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalResCase > 0 && (
                                    <div className="bg-yellow-50 p-4 rounded-lg">
                                        <p className="text-sm text-yellow-600">Total RES Case</p>
                                        <p className="text-2xl font-bold text-yellow-700">{stats.totalResCase.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalNorm > 0 && (
                                    <div className="bg-indigo-50 p-4 rounded-lg">
                                        <p className="text-sm text-indigo-500">Total NORM</p>
                                        <p className="text-2xl font-bold text-indigo-700">{stats.totalNorm.toLocaleString()}</p>
                                    </div>
                                )}

                                {stats && stats.totalRbPercentage > 0 && (
                                    <div className="bg-pink-50 p-4 rounded-lg">
                                        <p className="text-sm text-pink-500">Total RB %</p>
                                        <p className="text-2xl font-bold text-pink-700">{stats.totalRbPercentage.toLocaleString()}%</p>
                                    </div>
                                )}

                                {stats && stats.totalCollection > 0 && (
                                    <div className="bg-red-50 p-4 rounded-lg">
                                        <p className="text-sm text-red-500">Total Collection</p>
                                        <p className="text-2xl font-bold text-red-700">
                                            ₹{stats.totalCollection.toLocaleString()}
                                        </p>
                                    </div>
                                )}

                                {stats && stats.totalCurrentProvisionalBilling > 0 && (
                                    <div className="bg-teal-50 p-4 rounded-lg">
                                        <p className="text-sm text-teal-500">Current Provisional Billing</p>
                                        <p className="text-2xl font-bold text-teal-700">
                                            ₹{stats.totalCurrentProvisionalBilling.toLocaleString()}
                                        </p>
                                    </div>
                                )}

                                {stats && stats.totalFinalProvisionalBilling > 0 && (
                                    <div className="bg-orange-50 p-4 rounded-lg">
                                        <p className="text-sm text-orange-500">Final Provisional Billing</p>
                                        <p className="text-2xl font-bold text-orange-700">
                                            ₹{stats.totalFinalProvisionalBilling.toLocaleString()}
                                        </p>
                                    </div>
                                )}

                                {(!stats ||
                                    (stats.totalCases <= 0 &&
                                        stats.totalPos <= 0 &&
                                        stats.totalRes <= 0 &&
                                        stats.totalResCase <= 0 &&
                                        stats.totalNorm <= 0 &&
                                        stats.totalRbPercentage <= 0 &&
                                        stats.totalCollection <= 0 &&
                                        stats.totalCurrentProvisionalBilling <= 0 &&
                                        stats.totalFinalProvisionalBilling <= 0)) && (
                                        <div className="col-span-full text-center text-gray-500 py-4">
                                            No monthly statistics available yet. Start submitting your collections!
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search by process or month/year..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <select
                                    name="month"
                                    value={filters.month}
                                    onChange={handleFilterChange}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Months</option>
                                    {monthOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    name="year"
                                    value={filters.year}
                                    onChange={handleFilterChange}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Years</option>
                                    {yearOptions.map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilters({ month: '', year: '' });
                                        setPage(1);
                                    }}
                                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Collection Button */}
                <div className="flex justify-end items-center mb-4">
                    <button
                        onClick={() => {
                            setIsEditing(false);
                            setEditId(null);
                            const selectedProcess = assignedProcesses.length > 0 ? assignedProcesses[0]._id : '';
                            setFormData({
                                process: selectedProcess,
                                case: '',
                                pos: '',
                                res: '',
                                rescase: '',
                                norm: '',
                                rbPercentage: '',
                                totalCollection: '',
                                currentProvisionalBilling: '',
                                finalProvisionalBilling: ''
                            });

                            if (selectedProcess) {
                                dispatch(getProcessDefaults(selectedProcess));
                            }

                            setIsOpen(true);
                        }}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                        <ClipboardList size={18} />
                        Add Monthly Collection
                    </button>
                </div>

                {/* Success/Error Messages */}
                {(submitMonthlyCollectionData || updateMonthlyCollectionData || deleteMonthlyCollectionData) && (
                    <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {submitMonthlyCollectionData && 'Monthly collection submitted successfully!'}
                        {updateMonthlyCollectionData && 'Monthly collection updated successfully!'}
                        {deleteMonthlyCollectionData && 'Monthly collection deleted successfully!'}
                    </div>
                )}

                {(errorSubmitMonthlyCollection || errorUpdateMonthlyCollection || errorDeleteMonthlyCollection || errorMyMonthlyCollections) && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {errorSubmitMonthlyCollection || errorUpdateMonthlyCollection || errorDeleteMonthlyCollection || errorMyMonthlyCollections}
                    </div>
                )}

                {/* Modal for Add/Edit Collection */}
                {isOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-md w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                                    <ClipboardList className="mr-2" size={20} />
                                    {isEditing ? 'Update Monthly Collection' : 'Monthly Collection Form'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-500 hover:text-gray-800 font-bold text-xl"
                                    aria-label="Close modal"
                                >
                                    &times;
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Process</label>
                                    <select
                                        name="process"
                                        value={formData.process}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isEditing}
                                    >
                                        <option value="">Select Process</option>
                                        {assignedProcesses.map((process) => (
                                            <option key={process._id} value={process._id}>
                                                {process.process}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingProcessDefaults && (
                                        <p className="text-sm text-blue-500 mt-1">Loading defaults...</p>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">Case</label>
                                        <input
                                            type="number"
                                            name="case"
                                            value={formData.case}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder={processDefaultsData?.case ? `Default: ${processDefaultsData.case}` : ''}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">POS</label>
                                        <input
                                            type="number"
                                            name="pos"
                                            value={formData.pos}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder={processDefaultsData?.pos ? `Default: ${processDefaultsData.pos}` : ''}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RES%</label>
                                        <input
                                            type="number"
                                            name="res"
                                            value={formData.res}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RES Case</label>
                                        <input
                                            type="number"
                                            name="rescase"
                                            value={formData.rescase}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">NORM</label>
                                        <input
                                            type="number"
                                            name="norm"
                                            value={formData.norm}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-gray-700 font-medium mb-2">RB %</label>
                                        <input
                                            type="number"
                                            name="rbPercentage"
                                            value={formData.rbPercentage}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Total Collection</label>
                                    <input
                                        type="number"
                                        name="totalCollection"
                                        value={formData.totalCollection}
                                        onChange={handleChange}
                                        min="0"
                                        step="any"
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Current Provisional Billing</label>
                                    <input
                                        type="number"
                                        name="currentProvisionalBilling"
                                        value={formData.currentProvisionalBilling}
                                        onChange={handleChange}
                                        min="0"
                                        step="any"
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 font-medium mb-2">Final Provisional Billing</label>
                                    <input
                                        type="number"
                                        name="finalProvisionalBilling"
                                        value={formData.finalProvisionalBilling}
                                        onChange={handleChange}
                                        min="0"
                                        step="any"
                                        className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loadingSubmitMonthlyCollection || loadingUpdateMonthlyCollection}
                                    className={`w-full ${(loadingSubmitMonthlyCollection || loadingUpdateMonthlyCollection) ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"} text-white py-2 px-4 rounded-lg flex items-center justify-center transition-colors`}
                                >
                                    {(loadingSubmitMonthlyCollection || loadingUpdateMonthlyCollection) ? "Processing..." : isEditing ? "Update Collection" : "Submit Collection"}
                                    {!(loadingSubmitMonthlyCollection || loadingUpdateMonthlyCollection) && <ArrowRight className="ml-2" size={18} />}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Collections Table */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center">
                            <Calendar className="mr-2" size={20} />
                            {searchTerm ? 'Search Results' : 'Monthly Collections'}
                        </h2>
                    </div>

                    <div className="w-full overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5">
                                {loadingMyMonthlyCollections ? (
                                    <div className="p-6 text-center text-gray-500">Loading collections...</div>
                                ) : collections.length > 0 ? (
                                    <>
                                        <table className="min-w-[1400px] divide-y divide-gray-200 table-fixed">
                                            <thead className="bg-green-50 text-xs text-green-600 uppercase tracking-wider">
                                                <tr>
                                                    {[
                                                        { label: 'Month/Year', width: '130px' },
                                                        { label: 'Process', width: '140px' },
                                                        { label: 'Case', width: '90px' },
                                                        { label: 'POS', width: '90px' },
                                                        { label: 'RES%', width: '70px' },
                                                        { label: 'RES Case', width: '100px' },
                                                        { label: 'NORM', width: '90px' },
                                                        { label: 'RB %', width: '90px' },
                                                        { label: 'Total', width: '120px' },
                                                        { label: 'Current Provisional', width: '150px' },
                                                        { label: 'Final Provisional', width: '140px' },
                                                        { label: 'Actions', width: '120px' },
                                                    ].map(({ label, width }) => (
                                                        <th key={label} className="px-3 py-3 text-left font-medium" style={{ width }}>
                                                            {label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {collections.map((collection) => (
                                                    <tr key={collection._id} className="hover:bg-gray-50">
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '130px' }}>
                                                            {formatDate(collection.month, collection.year)}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '140px' }}>
                                                            {collection.process?.process}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '90px' }}>
                                                            {collection.case || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '90px' }}>
                                                            {collection.pos || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '70px' }}>
                                                            {collection.res || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm text-gray-700 truncate" style={{ width: '100px' }}>
                                                            {collection.rescase || 0}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-blue-600 truncate" style={{ width: '120px' }}>
                                                            ₹{(collection.totalCollection || 0).toLocaleString()}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-teal-600 truncate" style={{ width: '150px' }}>
                                                            ₹{(collection.currentProvisionalBilling || 0).toLocaleString()}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm font-semibold text-orange-600 truncate" style={{ width: '140px' }}>
                                                            ₹{(collection.finalProvisionalBilling || 0).toLocaleString()}
                                                        </td>
                                                        <td className="px-3 py-4 text-sm" style={{ width: '120px' }}>
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => handleUpdate(collection)}
                                                                    disabled={loadingUpdateMonthlyCollection}
                                                                    className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-xs transition-colors disabled:opacity-50"
                                                                >
                                                                    ✏️ Update
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(collection._id)}
                                                                    disabled={loadingDeleteMonthlyCollection}
                                                                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs transition-colors disabled:opacity-50"
                                                                >
                                                                    🗑️ Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Enhanced Pagination Component */}
                                        <EnhancedPagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalItems={totalItems}
                                            itemsPerPage={limit}
                                            onPageChange={handlePageChange}
                                            onItemsPerPageChange={handleItemsPerPageChange}
                                            maxVisiblePages={5}
                                            showItemsPerPage={true}
                                            showInfo={true}
                                        />
                                    </>
                                ) : (
                                    <div className="p-6 text-center text-gray-500">
                                        {searchTerm
                                            ? `No results found for "${searchTerm}". Try a different search term.`
                                            : 'No monthly collection data found. Start submitting your monthly collections!'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MonthlyCollection;